import {useDictionary, useClient} from '@adamlip/okeoke.client';
import {ButtonGroup, Drawer, DrawerBody, DrawerContent, DrawerFooter} from '@heroui/react';
import useStorage from 'hooks/useStorage';
import {useEffect, useState, useContext, useRef} from 'react';
import {toast} from 'react-hot-toast';
import {generatePin, loginAnonymous} from 'utils/User-v2';
import Reg from './Reg';
import EmailField from './EmailField';
import PinHandler from './PinHandler';
import Header from './Header';
import {UserContext} from 'contexts/UserContext';
import AnoIdentifier from './AnoIdentifier';
import {AppParamsContext} from 'contexts/AppParamsContext';
import {GoogleLogin} from '@react-oauth/google';
import axios from 'axios';
import BarSoftButton from 'components/_NewComponents/BarSoftButton';
import {LogIn, PanelBottomClose} from 'lucide-react';

export default function Login({isOpen, setIsOpen, forceVisible, canGuestLogin, onSuccessLogin}) {
	const {brandID} = useClient();

	const {appParams} = useContext(AppParamsContext);
	const {getValue} = useDictionary();
	const {setSV, removeSV, getSV} = useStorage();
	const {setToken} = useClient();
	const {setUserLoggedIn} = useContext(UserContext);

	const [loginType, setLoginType] = useState('reg');
	const [acceptedPolicy, setAcceptedPolicy] = useState(false);

	const [email, setEmail] = useState('');
	const [emailSaved, setEmailSaved] = useState(false);
	const [emailInvalid, setEmailInvalid] = useState(false);
	const [firstNameInvalid, setFirstNameInvalid] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const timer = useRef(null);

	const handleSetLoginType = (value) => {
		setLoginType(value);
	};

	const handleChangeEmail = (e) => {
		let value = e.target.value;
		setEmail(value);
		value === '' ? removeSV('login_email') : setSV('login_email', value);
		if (emailInvalid) checkEmailAddress(value);
	};

	const handleBlurEmail = (e) => {
		checkEmailAddress(e.target.value);
	};

	const handleClickOnSubmit = () => {
		if (emailSaved) {
			setEmailSaved(false);
		} else {
			switch (loginType) {
				case 'login':
					handleLogin();
					break;
				case 'reg':
					handleRegSubmit();
					break;
				case 'guest':
					handleGuestLogin();
					break;
				default:
					toast.error('app_v2_error');
			}
		}
	};

	const handleLogin = () => {
		let emailValid = checkEmailAddress(email);
		if (!emailValid) {
			toast.error(getValue('app_v2_invalid_email'));
			return null;
		}
		setEmailSaved(true);
		generatePin(email);
	};

	const handleRegSubmit = () => {
		let emailValid = checkEmailAddress(email);
		let loginType = appParams?.login?.shouldCheckBoxBeChecked;

		if (!emailValid) {
			toast.error(getValue('app_v2_invalid_email'));
			return null;
		}
		if (firstNameInvalid) {
			toast.error(getValue('app_v2_invalid_name'));
			return null;
		}
		if (loginType) {
			if (!acceptedPolicy) {
				toast.error(getValue('app_v2_login_policyError'));
				return null;
			}
		}
		setEmailSaved(true);
		generatePin(email);
	};

	const handleGuestLogin = () => {
		loginAnonymous(anoLoginCallback);
	};

	const anoLoginCallback = (res) => {
		if (res.success) {
			setToken(res.data.token);
			setUserLoggedIn(true);
			setSV('apiToken', res.data.token);
			setSV('apiTokenExpires', res.data.decoded.exp);
			//onSuccess();
		} else {
			toast.error(getValue('app_v2_toast_ano_error'));
		}
	};

	const googleLogin = (res) => {
		if (res.success) {
			setToken(res.data.token);
			setUserLoggedIn(true);
			setSV('apiToken', res.data.token);
			setSV('apiTokenExpires', res.data.expires);
			//onSuccess();
		} else {
			toast.error(getValue('app_v2_invalid_email'));
		}
	};

	const checkEmailAddress = (email) => {
		const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
		const valid = re.test(String(email).toLowerCase());
		setEmailInvalid(!valid);
		return valid;
	};

	const onSuccess = () => {
		setIsOpen(false);
		if (typeof onSuccessLogin === 'function') {
			onSuccessLogin();
		}
	};

	useEffect(() => {
		if (isOpen) {
			timer.current = setTimeout(() => {
				setLoaded(true);
			}, 300);
		} else {
			clearTimeout(timer.current);
			setLoaded(false);
		}
	}, [isOpen]);

	useEffect(() => {
		setEmail(getSV('login_email'));
		//eslint-disable-next-line
	}, []);

	useEffect(() => {
		const handleKeyPress = (event) => {
			if (event.key === 'Enter') {
				if (isOpen && email && email !== '') {
					handleClickOnSubmit();
				}
			}
		};
		window.addEventListener('keydown', handleKeyPress);
		return () => {
			window.removeEventListener('keydown', handleKeyPress);
		};
	}, [email, isOpen, emailSaved, loginType]);

	return (
		<Drawer
			isOpen={isOpen}
			onOpenChange={setIsOpen}
			placement='bottom'
			size='full'
			hideCloseButton
			classNames={{
				closeButton: 'z-[9999] bg-primary text-primary-foreground',
			}}>
			<DrawerContent>
				{(onClose) => (
					<>
						<DrawerBody className='overflow-y-auto overflow-hidden grow gap-0 flex flex-col items-start justify-center  w-full'>
							<h2 className='text-center text-xl w-full font-bold'>
								{!forceVisible && getValue('app_v2_need_to_login')}
								{forceVisible && getValue('app_v2_welcome')}
							</h2>
							<div className='font-normal text-sm text-center opacity-70 pb-4'>
								{!forceVisible && getValue('app_v2_need_to_login_desc')}
								{forceVisible && getValue('app_v2_welcome_desc')}
							</div>
							<div className='w-full flex flex-col gap-2'>
								{!emailSaved && (
									<ButtonGroup fullWidth>
										<BarSoftButton variant={`${loginType === 'reg' ? 'solid' : 'flat'}`} color={`${loginType === 'reg' ? 'primary' : 'default'}`} onPress={() => handleSetLoginType('reg')}>
											{getValue('app_v2_login_reg')}
										</BarSoftButton>
										<BarSoftButton variant={`${loginType === 'login' ? 'solid' : 'flat'}`} color={`${loginType === 'login' ? 'primary' : 'default'}`} onPress={() => handleSetLoginType('login')}>
											{getValue('app_v2_login_login')}
										</BarSoftButton>
										{appParams?.login?.anoLoginEnabled && !getSV('anoLoginUUID') && canGuestLogin && (
											<BarSoftButton variant={`${loginType === 'guest' ? 'solid' : 'flat'}`} color={`${loginType === 'guest' ? 'warning' : 'default'}`} onPress={() => handleSetLoginType('guest')}>
												{getValue('app_v2_login_guest')}
											</BarSoftButton>
										)}
									</ButtonGroup>
								)}

								<Header loginType={loginType} emailSaved={emailSaved} />
								<EmailField handleChangeEmail={handleChangeEmail} handleBlurEmail={handleBlurEmail} emailInvalid={emailInvalid} email={email} visible={loginType !== 'guest' && !emailSaved} />
								{loaded && <Reg firstNameInvalid={firstNameInvalid} setFirstNameInvalid={setFirstNameInvalid} visible={loginType === 'reg' && !emailSaved} acceptedPolicy={acceptedPolicy} setAcceptedPolicy={setAcceptedPolicy} />}

								{loginType !== 'guest' && emailSaved && <PinHandler email={email} onSuccess={onSuccess} />}
								{loginType === 'guest' && (
									<>
										<div>{getValue('app_v2_guest_login_disclaimer')}</div>
										<AnoIdentifier />
									</>
								)}

								{appParams?.login?.googleLoginEnabled && (
									<div className='w-full flex justify-center'>
										<GoogleLogin
											onSuccess={(result) => {
												axios
													.post(`https://brands-dev.app.okeoke.io/api/v1/users/login/google`, {
														credential: result.credential,
														brandID: brandID,
														deviceUUID: '',
													})
													.then((res) => {
														googleLogin(res.data);
													})
													.catch((err) => {
														toast.error(getValue('app_v2_invalid_email'));
														console.log(err);
													});
											}}
											onError={console.log}
										/>
									</div>
								)}
							</div>
						</DrawerBody>
						<DrawerFooter className='w-full flex flex-col gap-2'>
							<BarSoftButton color='primary' fullWidth className={` py-3  `} onPress={handleClickOnSubmit} forceCustom={true} forceHideEndIcon variant='solid' endContent={<LogIn className='w-4 h-4' />}>
								{!emailSaved && getValue('app_v2_login_loginBtn')}
								{emailSaved && <div>{`${email} ${getValue('app_v2_login_loginBtn_modify')}`}</div>}
							</BarSoftButton>
							{!forceVisible && (
								<BarSoftButton color='primary' fullWidth onPress={onClose} forceCustom={true} variant='bordered' forceHideEndIcon endContent={<PanelBottomClose className='w-4 h-4' />}>
									{getValue('app_v2_close')}
								</BarSoftButton>
							)}
						</DrawerFooter>
					</>
				)}
			</DrawerContent>
		</Drawer>
	);
}
